import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export const RevealLines = ({ lines, as = "h1", className = "", play = true, delay = 0, testId }) => {
  const Tag = motion[as];
  return (
    <Tag className={className} data-testid={testId}>
      {lines.map((l, i) => (
        <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.06em]">
          <motion.span
            className={`block ${l.className ?? ""}`}
            initial={{ y: "110%" }}
            animate={{ y: play ? "0%" : "110%" }}
            transition={{ duration: 1.1, ease: EASE, delay: delay + i * 0.13 }}
          >
            {l.text}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

export const FadeUp = ({ children, className = "", delay = 0, y = 32 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

export const ParallaxImage = ({ src, alt, className = "", imgClassName = "", strength = 10 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${strength}%`, `${strength}%`]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={alt ? src : src} alt={alt} style={{ y, scale: 1 + strength / 50 }} className={`absolute inset-0 h-full w-full object-cover ${imgClassName}`} />
    </div>
  );
};

export const Tilt = ({ children, className = "", max = 8 }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [max, -max]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-max, max]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div style={{ perspective: 1200 }} className={className}>
      <motion.div onMouseMove={onMove} onMouseLeave={reset} style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="h-full w-full">
        {children}
      </motion.div>
    </div>
  );
};
